/**
 * AppPickerTrigger — single-select field using the same underline + typography as `Input`,
 * with a trailing chevron (only visual difference vs a plain text field).
 * @see Input presentation="selector"
 */
import React from 'react';
import { StyleSheet, ViewStyle } from 'react-native';
import { SvgIcon } from './SvgIcon';
import { Clickable } from './Clickable';
import { Input } from './Input';
import { ButtonLoader } from '../load-main/ButtonLoader';
import { appTheme, useThemeColors } from '../../shared/theme';
import { useRtl } from '../../shared/i18n/useRtl';

export interface AppPickerTriggerProps {
  label?: string;
  value: string | null;
  placeholder: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  containerStyle?: ViewStyle;
  accessibilityLabel?: string;
}

export const AppPickerTrigger: React.FC<AppPickerTriggerProps> = ({
  label,
  value,
  placeholder,
  onPress,
  disabled = false,
  loading = false,
  error,
  containerStyle,
  accessibilityLabel,
}) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();

  return (
    <Clickable
      onPress={onPress}
      disabled={disabled || loading}
      style={styles.clickable}
      accessibilityLabel={accessibilityLabel ?? value ?? placeholder}
      accessibilityRole="button">
      <Input
        accessible={false}
        label={label}
        placeholder={placeholder}
        value={value ?? ''}
        presentation="selector"
        disabled={disabled}
        error={error}
        containerStyle={[styles.inputContainer, containerStyle]}
        inputStyle={{ textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }}
        leftAccessory={loading ? <ButtonLoader size={18} color={colors.primary}  /> : undefined}
        rightAccessory={loading ? undefined : <SvgIcon name="arrow-down" size={20} color={colors.textSecondary} />}
      />
    </Clickable>
  );
};

const styles = StyleSheet.create({
  clickable: {
    alignSelf: 'stretch',
  },
  inputContainer: {
    marginBottom: 0,
  },
});

export default AppPickerTrigger;
